// Pure helpers for turning the backend's columnar /pumps payload into
// Highcharts-ready, min/max-downsampled series while retaining the full-
// resolution samples (as typed arrays) for zoom.
//
// Kept free of React/Highcharts imports so it can be moved into a Web Worker
// unchanged (Phase 3): the same functions run on the worker thread and post
// back the typed arrays / plain [x, y] tuples via structured clone / transfer.

import { toWholeNumberReading, type PumpChannel } from "./pumpChannels.ts";

// Columnar response shape from GET /pumps?...&format=columnar:
//   { channels: string[], series: { [pump_name]: { t: number[], [chan]: (number|null)[] } }, ... }
export interface ColumnarPumpsResponse {
  resolution?: string;
  bucket_ms?: number;
  start_time?: number;
  end_time?: number;
  row_count?: number;
  truncated?: boolean;
  channels?: string[];
  series?: Record<string, Record<string, Array<number | null>>>;
}

// Full-resolution samples for one (pump, channel), null values already dropped.
export interface ChannelBuffer {
  t: Float64Array; // timestamps (ms), ascending
  v: Float32Array; // values, aligned with t
}

// pump_name -> channel value -> full-res buffer
export type FleetBuffers = Map<string, Map<string, ChannelBuffer>>;

export type AxisSettingsLike = Record<string, { channels: string[] }>;

const AXIS_INDEX: Record<string, number> = {
  left1: 0,
  left2: 1,
  right1: 2,
  right2: 3,
};

// Default point budget per series. Screen is ~1-2k px wide; min/max keeps 2
// points per bucket, so ~2000 points ≈ 1000 buckets — one per pixel column.
export const DEFAULT_MAX_POINTS = 2000;

export const yAxisIndexForChannel = (
  channelValue: string,
  axisSettings: AxisSettingsLike,
): number => {
  const axisName =
    Object.keys(axisSettings).find((axis) =>
      axisSettings[axis]?.channels?.includes(channelValue),
    ) || "left1";
  return AXIS_INDEX[axisName] ?? 0;
};

// Parse the columnar payload into per-(pump, channel) typed buffers, dropping
// null/NaN samples per channel (a channel can be null at timestamps where
// others are present). Only the requested channels are materialized.
export const parseColumnarToBuffers = (
  payload: ColumnarPumpsResponse,
  channelValues: string[],
): FleetBuffers => {
  const buffers: FleetBuffers = new Map();
  const series = payload?.series;
  if (!series) return buffers;

  for (const pumpName of Object.keys(series)) {
    const pumpCols = series[pumpName];
    const ts = pumpCols?.t;
    if (!ts || ts.length === 0) continue;

    const channelMap = new Map<string, ChannelBuffer>();

    for (const channel of channelValues) {
      const col = pumpCols[channel];
      if (!col) continue;

      const n = Math.min(ts.length, col.length);

      // First pass: count non-null samples so we can size typed arrays exactly.
      let count = 0;
      for (let i = 0; i < n; i++) {
        const value = col[i];
        if (value !== null && value !== undefined && !Number.isNaN(value as number)) {
          count++;
        }
      }
      if (count === 0) continue;

      const t = new Float64Array(count);
      const v = new Float32Array(count);
      let j = 0;
      for (let i = 0; i < n; i++) {
        const value = col[i];
        if (value !== null && value !== undefined && !Number.isNaN(value as number)) {
          t[j] = ts[i] as number;
          v[j] = toWholeNumberReading(channel, value as number);
          j++;
        }
      }

      channelMap.set(channel, { t, v });
    }

    if (channelMap.size > 0) buffers.set(pumpName, channelMap);
  }

  return buffers;
};

// Build full-resolution FleetBuffers from plain pump ROWS (e.g. the live
// streaming rolling window in pumpDataByFleet), keyed by the given canonical
// channel values. `resolveValue(row, channelValue)` returns the numeric reading
// for a channel on a row (the caller supplies alias resolution). Produces the
// same buffer shape a columnar /pumps load yields, so a streamed window can be
// "frozen" into buffers — used to keep the chart on stop and the popout gap-free.
export const buildBuffersFromRows = (
  rows: Array<Record<string, unknown>>,
  channelValues: string[],
  resolveValue: (row: Record<string, unknown>, channelValue: string) => unknown,
): FleetBuffers => {
  const buffers: FleetBuffers = new Map();
  if (!Array.isArray(rows) || rows.length === 0 || channelValues.length === 0) {
    return buffers;
  }

  // Collect [t, v] samples per (pump, channel).
  const collected = new Map<string, Map<string, Array<[number, number]>>>();
  for (const row of rows) {
    const pumpName = String((row?.pump_name as string) ?? "").trim();
    const t = Number(row?.time_stamp);
    if (!pumpName || !Number.isFinite(t)) continue;

    let channelMap = collected.get(pumpName);
    if (!channelMap) {
      channelMap = new Map();
      collected.set(pumpName, channelMap);
    }

    for (const channel of channelValues) {
      const raw = resolveValue(row, channel);
      if (raw === null || raw === undefined) continue;
      const value = toWholeNumberReading(channel, Number(raw));
      if (Number.isNaN(value)) continue;

      let samples = channelMap.get(channel);
      if (!samples) {
        samples = [];
        channelMap.set(channel, samples);
      }
      samples.push([t, value]);
    }
  }

  collected.forEach((channelMap, pumpName) => {
    const resolved = new Map<string, ChannelBuffer>();
    channelMap.forEach((samples, channel) => {
      // Rows may be unsorted; sort ascending and dedup exact timestamps.
      samples.sort((a, b) => a[0] - b[0]);
      const t: number[] = [];
      const v: number[] = [];
      for (const [time, value] of samples) {
        if (t.length > 0 && t[t.length - 1] === time) {
          v[v.length - 1] = value;
        } else {
          t.push(time);
          v.push(value);
        }
      }
      if (t.length > 0) {
        resolved.set(channel, {
          t: Float64Array.from(t),
          v: Float32Array.from(v),
        });
      }
    });
    if (resolved.size > 0) buffers.set(pumpName, resolved);
  });

  return buffers;
};

// Append new row samples to existing full-res buffers (per pump/channel), then
// trim every buffer to [windowStart, windowEnd]. Keeps full 1s resolution so the
// LIVE chart can render through the exact same buildDownsampledSeries path (and
// line style) as a static load — no separate row-based builder, no divergence.
// `resolveValue` maps a row + canonical channel value to its numeric reading.
export const appendSamplesToBuffers = (
  existing: FleetBuffers | undefined,
  rows: Array<Record<string, unknown>>,
  channelValues: string[],
  resolveValue: (row: Record<string, unknown>, channelValue: string) => unknown,
  windowStart: number,
  windowEnd: number,
): FleetBuffers => {
  // Collect incoming samples per pump/channel (within the window).
  const incoming = new Map<string, Map<string, Array<[number, number]>>>();
  if (Array.isArray(rows)) {
    for (const row of rows) {
      const pumpName = String((row?.pump_name as string) ?? "").trim();
      const t = Number(row?.time_stamp);
      if (!pumpName || !Number.isFinite(t) || t < windowStart || t > windowEnd) {
        continue;
      }
      let channelMap = incoming.get(pumpName);
      if (!channelMap) {
        channelMap = new Map();
        incoming.set(pumpName, channelMap);
      }
      for (const channel of channelValues) {
        const raw = resolveValue(row, channel);
        if (raw === null || raw === undefined) continue;
        const value = Number(raw);
        if (Number.isNaN(value)) continue;
        let samples = channelMap.get(channel);
        if (!samples) {
          samples = [];
          channelMap.set(channel, samples);
        }
        samples.push([t, value]);
      }
    }
  }

  // Merge existing (trimmed to window) + incoming, dedup by timestamp, per
  // pump/channel — over the union of pumps so stalled pumps roll off the left.
  const out: FleetBuffers = new Map();
  const pumpNames = new Set<string>([
    ...Array.from(existing?.keys() ?? []),
    ...Array.from(incoming.keys()),
  ]);
  pumpNames.forEach((pumpName) => {
    const oldChannelMap = existing?.get(pumpName);
    const incomingChannelMap = incoming.get(pumpName);
    const channels = new Set<string>([
      ...Array.from(oldChannelMap?.keys() ?? []),
      ...Array.from(incomingChannelMap?.keys() ?? []),
    ]);
    const resolved = new Map<string, ChannelBuffer>();
    channels.forEach((channel) => {
      const merged = new Map<number, number>();
      const old = oldChannelMap?.get(channel);
      if (old) {
        for (let i = 0; i < old.t.length; i += 1) {
          const time = old.t[i];
          if (time >= windowStart && time <= windowEnd) merged.set(time, old.v[i]);
        }
      }
      (incomingChannelMap?.get(channel) ?? []).forEach(([time, value]) => {
        if (time >= windowStart && time <= windowEnd) merged.set(time, value);
      });
      if (merged.size === 0) return;
      const times = Array.from(merged.keys()).sort((a, b) => a - b);
      resolved.set(channel, {
        t: Float64Array.from(times),
        v: Float32Array.from(times.map((time) => merged.get(time) as number)),
      });
    });
    if (resolved.size > 0) out.set(pumpName, resolved);
  });

  return out;
};

// Min/max-per-bucket downsample. Preserves local extrema (spikes) — critical
// for telemetry reviewed for interlock/alarm events, unlike LTTB which can
// smooth a single-sample spike away. Returns [x, y] tuples in time order.
export const minMaxDownsample = (
  buffer: ChannelBuffer,
  maxPoints: number = DEFAULT_MAX_POINTS,
): [number, number][] => {
  const { t, v } = buffer;
  const n = t.length;
  if (n === 0) return [];

  if (n <= maxPoints) {
    const out: [number, number][] = new Array(n);
    for (let i = 0; i < n; i++) out[i] = [t[i], v[i]];
    return out;
  }

  const buckets = Math.max(1, Math.floor(maxPoints / 2));
  const bucketSize = n / buckets;
  const out: [number, number][] = [];

  out.push([t[0], v[0]]); // always keep the first sample

  for (let b = 0; b < buckets; b++) {
    const start = Math.floor(b * bucketSize);
    const end = Math.min(n, Math.floor((b + 1) * bucketSize));
    if (start >= end) continue;

    let minI = start;
    let maxI = start;
    for (let i = start + 1; i < end; i++) {
      if (v[i] < v[minI]) minI = i;
      if (v[i] > v[maxI]) maxI = i;
    }

    // Emit the two extrema in chronological order so the line stays monotonic in x.
    if (minI === maxI) {
      out.push([t[minI], v[minI]]);
    } else if (minI < maxI) {
      out.push([t[minI], v[minI]]);
      out.push([t[maxI], v[maxI]]);
    } else {
      out.push([t[maxI], v[maxI]]);
      out.push([t[minI], v[minI]]);
    }
  }

  out.push([t[n - 1], v[n - 1]]); // always keep the last sample
  return out;
};

// Typical spacing between samples (median of up to 2000 deltas): 1000 for 1s
// data, 60000 for 1m rollups. Used to tell a real outage from normal cadence.
const typicalSampleInterval = (t: Float64Array): number => {
  const n = Math.min(t.length, 2001);
  if (n < 2) return 1000;
  const deltas: number[] = [];
  for (let i = 1; i < n; i++) {
    const delta = t[i] - t[i - 1];
    if (delta > 0) deltas.push(delta);
  }
  if (deltas.length === 0) return 1000;
  deltas.sort((a, b) => a - b);
  return deltas[Math.floor(deltas.length / 2)];
};

// Same min/max downsample as minMaxDownsample, but the line breaks wherever
// the pump stopped reporting: a null point is inserted at every gap longer
// than 5x the normal sample spacing (at least 5 s). Without this, Highcharts
// draws straight diagonal/flat lines across outages. For chart rendering only;
// callers that need plain numbers should keep using minMaxDownsample.
export const minMaxDownsampleWithGaps = (
  buffer: ChannelBuffer,
  maxPoints: number = DEFAULT_MAX_POINTS,
): [number, number | null][] => {
  const { t, v } = buffer;
  const n = t.length;
  if (n === 0) return [];

  const gapMs = Math.max(5000, typicalSampleInterval(t) * 5);

  // Split into gap-free runs; each gets a share of the point budget in
  // proportion to its length so short runs are not starved.
  const runStarts: number[] = [0];
  for (let i = 1; i < n; i++) {
    if (t[i] - t[i - 1] > gapMs) runStarts.push(i);
  }

  if (runStarts.length === 1) return minMaxDownsample(buffer, maxPoints);

  const out: [number, number | null][] = [];
  for (let r = 0; r < runStarts.length; r++) {
    const start = runStarts[r];
    const end = r + 1 < runStarts.length ? runStarts[r + 1] : n;
    if (r > 0) out.push([t[start - 1] + 1, null]);

    const budget = Math.max(2, Math.round((maxPoints * (end - start)) / n));
    const run = minMaxDownsample(
      { t: t.subarray(start, end), v: v.subarray(start, end) },
      budget,
    );
    for (const point of run) out.push(point);
  }
  return out;
};

// Tuple version of the gap handling in minMaxDownsampleWithGaps, for paths that
// build [x, y] arrays from rows: inserts a null after any gap longer than 5x
// the normal sample spacing (at least 5 s) so the line breaks there.
export const insertGapBreaks = (
  points: [number, number][],
): [number, number | null][] => {
  if (points.length < 2) return points;

  const sampleTimes = new Float64Array(Math.min(points.length, 2001));
  for (let i = 0; i < sampleTimes.length; i++) sampleTimes[i] = points[i][0];
  const gapMs = Math.max(5000, typicalSampleInterval(sampleTimes) * 5);

  const out: [number, number | null][] = [points[0]];
  for (let i = 1; i < points.length; i++) {
    if (points[i][0] - points[i - 1][0] > gapMs) {
      out.push([points[i - 1][0] + 1, null]);
    }
    out.push(points[i]);
  }
  return out;
};

// Min/max-per-bucket downsample for an already-sorted array of [x, y] tuples.
// Same algorithm/semantics as minMaxDownsample, but for the live-streaming path,
// which accumulates tuples rather than typed buffers. Returns the input array
// unchanged when it's already within budget.
export const minMaxDownsampleTuples = (
  points: [number, number][],
  maxPoints: number = DEFAULT_MAX_POINTS,
): [number, number][] => {
  const n = points.length;
  if (n <= maxPoints) return points;

  const buckets = Math.max(1, Math.floor(maxPoints / 2));
  const bucketSize = n / buckets;
  const out: [number, number][] = [];

  out.push(points[0]);

  for (let b = 0; b < buckets; b++) {
    const start = Math.floor(b * bucketSize);
    const end = Math.min(n, Math.floor((b + 1) * bucketSize));
    if (start >= end) continue;

    let minI = start;
    let maxI = start;
    for (let i = start + 1; i < end; i++) {
      if (points[i][1] < points[minI][1]) minI = i;
      if (points[i][1] > points[maxI][1]) maxI = i;
    }

    if (minI === maxI) {
      out.push(points[minI]);
    } else if (minI < maxI) {
      out.push(points[minI]);
      out.push(points[maxI]);
    } else {
      out.push(points[maxI]);
      out.push(points[minI]);
    }
  }

  out.push(points[n - 1]);
  return out;
};

export interface DownsampledSeries {
  name: string;
  pumpName: string;
  channelValue: string;
  yAxis: number;
  // null entries mark gaps in the telemetry (the line breaks there).
  data: [number, number | null][];
  visible: boolean;
  isSinglePoint: boolean;
}

// Build one downsampled series per (visible pump, channel) from full-res
// buffers. `windowStart`/`windowEnd` (optional) restrict downsampling to a
// zoomed subrange without a refetch — the full-res buffer stays in memory.
export const buildDownsampledSeries = (
  buffers: FleetBuffers,
  selectedPumps: string[],
  hiddenPumps: Set<string>,
  channels: PumpChannel[],
  axisSettings: AxisSettingsLike,
  maxPoints: number = DEFAULT_MAX_POINTS,
): DownsampledSeries[] => {
  const out: DownsampledSeries[] = [];

  for (const pumpName of selectedPumps) {
    const channelMap = buffers.get(pumpName);
    if (!channelMap) continue;

    for (const channel of channels) {
      const buffer = channelMap.get(channel.value);
      if (!buffer || buffer.t.length === 0) continue;

      const data = minMaxDownsampleWithGaps(buffer, maxPoints);
      if (data.length === 0) continue;

      out.push({
        name: `${pumpName} - ${channel.label}`,
        pumpName,
        channelValue: channel.value,
        yAxis: yAxisIndexForChannel(channel.value, axisSettings),
        data,
        visible: !hiddenPumps.has(pumpName),
        isSinglePoint: data.length <= 1,
      });
    }
  }

  return out;
};

// Slice a full-res buffer to a [start, end] window (inclusive), returning a
// view-backed buffer for re-downsampling on zoom. t is ascending so we binary
// search the bounds.
export const sliceBuffer = (
  buffer: ChannelBuffer,
  start: number,
  end: number,
): ChannelBuffer => {
  const { t, v } = buffer;
  const n = t.length;
  if (n === 0) return buffer;

  const lo = lowerBound(t, start);
  const hi = upperBound(t, end);
  if (lo >= hi) return { t: new Float64Array(0), v: new Float32Array(0) };

  return { t: t.subarray(lo, hi), v: v.subarray(lo, hi) };
};

function lowerBound(arr: Float64Array, target: number): number {
  let lo = 0;
  let hi = arr.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (arr[mid] < target) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

function upperBound(arr: Float64Array, target: number): number {
  let lo = 0;
  let hi = arr.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (arr[mid] <= target) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}
